/**
 * O seletor de quem atende a conversa.
 *
 * ── Por que deixou de ser botão que abre diálogo ─────────────────────────
 *
 * Era um nome e um botão "Trocar", e a lista de gente só aparecia depois de um
 * clique, dentro de um diálogo. Ver quem existe é a metade mais usada da
 * pergunta — "para quem eu passo isto?" — e ela estava escondida atrás de uma
 * janela que também pedia outras coisas.
 *
 * Agora os nomes estão no próprio painel, como no campo de prioridade ao lado.
 *
 * ── E por que o motivo continua existindo ────────────────────────────────
 *
 * Porque o contrato exige, e a razão está escrita nele: *"transferência sem
 * motivo registrado é o tipo de coisa que ninguém consegue reconstruir três
 * meses depois, quando a pergunta for 'por que este cliente mudou de
 * vendedor?'"*.
 *
 * Um seletor que gravasse na hora tomaria 400 do servidor, e a conversa ficaria
 * com o nome novo na tela e o dono antigo no motor. O motivo aparece ABAIXO do
 * seletor depois da escolha, e não numa janela: a escolha continua visível
 * enquanto se escreve, e dá para trocar de ideia sem fechar nada.
 *
 * ── Quem NÃO pode receber aparece, e desabilitado ────────────────────────
 *
 * Esconder essas pessoas responderia "não existe" a quem pergunta "cadê o
 * Fulano?" — e a resposta certa é "existe, e falta isto". Cada uma vem com o
 * obstáculo no próprio rótulo, que é o que a lista do diálogo já fazia.
 */

import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi, useResponsaveisPossiveis } from '@/shared/api/consultas';
import { chavesDaPassagemDeConversa } from '@/shared/api/chaves';
import { Botao, Campo, CampoEscolha } from '@/shared/ui';

/** O obstáculo em três palavras, ao lado do nome — o mesmo texto do diálogo. */
const EXPLICACAO_DO_MOTIVO: Readonly<Record<string, string>> = {
  sem_acesso_ao_atendimento: 'ainda não tem acesso ao atendimento',
  sem_cadastro_no_crm: 'está com o cadastro incompleto',
  vinculo_suspenso: 'está com o acesso suspenso',
};

const MOTIVO_MINIMO = 3;

export function SeletorDeResponsavel({
  conversaId,
  nomeAtual,
  rotuloOculto = false,
}: {
  conversaId: string;
  /** Quem a CONVERSA diz que atende, para a espera não mentir. `null` = ninguém. */
  nomeAtual: string | null;
  /** Só para o leitor de tela: a linha da ficha já escreve "Responsável". */
  rotuloOculto?: boolean;
}): ReactNode {
  const rotulos = rotuloOculto
    ? { rotuloOculto: 'Agente atribuído' }
    : { rotulo: 'Agente atribuído' };
  const api = useApi();
  const cache = useQueryClient();
  const lista = useResponsaveisPossiveis(conversaId, true);

  /** `null` = nada escolhido ainda; `''` = escolheu "Ninguém" (desatribuir). */
  const [escolhido, definirEscolhido] = useState<string | null>(null);
  const [motivo, definirMotivo] = useState('');
  const [erro, definirErro] = useState<string | null>(null);

  /*
   * A chave nasce com a ESCOLHA, e não com o clique em "Passar".
   *
   * É o que faz o duplo clique — e o retry depois de um erro de rede — cair na
   * mesma chave, em vez de registrar duas passagens de mão do mesmo fato. Mesma
   * razão do diálogo, que a amarrava à abertura.
   */
  const [chave, definirChave] = useState(() => crypto.randomUUID());

  const itens = lista.data?.itens ?? [];
  const atual = itens.find((pessoa) => pessoa.atual) ?? null;
  const valorNoCampo = escolhido ?? atual?.usuario_id ?? '';

  function desistir(): void {
    definirEscolhido(null);
    definirMotivo('');
    definirErro(null);
  }

  const passar = useMutation({
    mutationFn: () => {
      if (!api) throw new Error('sem sessão');
      return api.transferirConversa({
        caminho: { id: conversaId },
        corpo: { usuario_id: escolhido === '' ? null : escolhido, motivo: motivo.trim() },
        idempotencyKey: chave,
      });
    },
    onMutate: () => definirErro(null),
    onSuccess: async () => {
      /*
       * A conversa, a fila E a lista de responsáveis — ver
       * `chavesDaPassagemDeConversa`.
       *
       * ESPERANDO a busca antes de limpar a escolha. `desistir` devolve o
       * campo ao "atual" da lista; limpar antes de ela voltar mostraria, por
       * um instante, a pessoa de antes, que é o defeito que se via.
       */
      await Promise.all(
        chavesDaPassagemDeConversa(conversaId).map((queryKey) =>
          cache.invalidateQueries({ queryKey }),
        ),
      );
      // Chave nova: a próxima passagem é outro fato.
      definirChave(crypto.randomUUID());
      desistir();
    },
    onError: () =>
      definirErro(
        'Não foi possível passar a conversa. Ela continua com quem estava — tente de novo.',
      ),
  });

  const escolheu = escolhido !== null;
  const podeConfirmar = motivo.trim().length >= MOTIVO_MINIMO && !passar.isPending;

  return (
    <div className="responsavel">
      {/*
        Enquanto a lista não chega, o campo mostra QUEM ATENDE — e não "Ninguém".
 
        A lista vem da API e é ela que sabe quem pode receber; a conversa já
        traz o nome de quem atende. Sem esta espera, o seletor nasceria em
        "Ninguém" e, por um instante, diria que a conversa não tem dono — que é
        o mesmo campo em branco lido como defeito que o painel evita em toda
        parte.
      */}
      {lista.isPending ? (
        <CampoEscolha
          {...rotulos}
          valor="atual"
          opcoes={[{ valor: 'atual', rotulo: nomeAtual ?? 'Ninguém' }]}
          aoEscolher={() => undefined}
          desabilitado
        />
      ) : (
        <CampoEscolha
          {...rotulos}
          valor={valorNoCampo}
          desabilitado={passar.isPending || api === null}
          ajuda={passar.isPending ? 'passando…' : undefined}
          aoEscolher={(valor) => {
            definirEscolhido(valor);
            definirErro(null);
          }}
          opcoes={[
            /*
              "Ninguém" é opção, e não o vazio do seletor: devolver a conversa à
              fila é uma escolha, e é justamente o estado que a aba "Não
              atribuídas" existe para achar.
            */
            { valor: '', rotulo: 'Ninguém' },
            /*
              Só o nome, e não o papel: "admin_tenant" é nome de dentro do
              sistema, e quem escolhe para quem passar procura a pessoa. Quem
              NÃO pode receber continua com o porquê, que é o que explica a
              opção apagada.
            */
            ...itens.map((pessoa) => ({
              valor: pessoa.usuario_id,
              rotulo: pessoa.nome,
              desabilitada: !pessoa.pode_receber,
              detalhe: pessoa.pode_receber
                ? undefined
                : (EXPLICACAO_DO_MOTIVO[pessoa.motivo ?? ''] ?? 'não pode receber'),
            })),
          ]}
        />
      )}

      {/*
        O motivo só aparece depois da escolha.

        Antes dela não há o que justificar, e um campo vazio permanente no
        painel leria como coisa a preencher — numa coluna estreita, cada linha
        que não serve empurra a próxima para fora da tela.
      */}
      {escolheu && (
        <div className="responsavel__confirmar">
          <Campo
            rotulo="Por que está passando?"
            value={motivo}
            maxLength={280}
            placeholder="ex.: cliente da região do Paulo"
            onChange={(evento) => definirMotivo(evento.target.value)}
            ajuda={
              motivo.trim().length > 0 && motivo.trim().length < MOTIVO_MINIMO
                ? 'Diga em poucas palavras — quem ler depois vai precisar.'
                : undefined
            }
            erro={erro ?? undefined}
          />
          <div className="responsavel__acoes">
            <Botao
              miudo
              onClick={() => passar.mutate()}
              desabilitado={!podeConfirmar}
              motivo="Escreva o motivo antes de passar."
            >
              Passar
            </Botao>
            <Botao
              miudo
              variante="fantasma"
              onClick={desistir}
              desabilitado={passar.isPending}
              // Botão cinza sem motivo gera chamado no suporte: a regra do
              // sistema visual é que desabilitado vem sempre com o porquê.
              motivo="A transferência está em curso — espere ela terminar."
            >
              Cancelar
            </Botao>
          </div>
        </div>
      )}
    </div>
  );
}
